import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import B2BLayout from '../../../layouts/b2b'
import { Button, message, Modal, Result, Space } from 'antd'
import { CarOutlined, EyeInvisibleOutlined } from '@ant-design/icons'
import { getColumns } from './columns'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import { Sorter } from '../../../interfaces'
import { declension, objectToUrl } from '../../utils'
import { currentFiltersProps, DataType, Enums, IStage, TablePaginationPosition } from './interface'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import './index.css'
import ActionButtons from './action-buttons'
import { VehicleModal } from '../card/modals/modal-vehicle'
import FiltersDrawer from '../../../components/v2/filters-drawer'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import { HStack } from 'src/react-app/ui/Stack'
import { DeliveriesPrintButton } from '../ui/deliveries-print-button/deliveries-print-button'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import { UnloadFromWDModal } from '../card/modals/modal-unload-from-wd'
import { VehicleSummaryTiles } from './vehicle-summary'
import {
  handleCancelDeliveryDateModal,
  handleCancelPyramidModal,
  handleCancelUnloadFromWDModal,
  handleCancelVehicleModal,
  handleCreate,
  handleOkDeliveryDateModal,
  handleOkPyramidModal,
  handleOkUnloadFromWDModal,
  handleOkVehicleModal,
  handleSaveEdit,
  handleSendMail,
  handleSendToSchedex,
  handleSetDeliveryDateModal,
  handleSetPlanned,
  handleSetPyramidModal,
  handleSetVehicleModal,
  handleUnloadFromSchedex,
  handleUnloadFromWD,
  handleUpload,
  isActionButtonEnabled,
} from './actions'
import DateFilter from './filter-date'
import { getDataOfType } from '../../../../lib/utils/get-data-of-type'
import { FilterFastButtons } from '../ui/filter-buttons/filter-fast-buttons'
import FixedHeader from '../../../layouts/b2b/fixed-header'
import DataTable from '../../../components/data-table'
import { PyramidModal } from '../card/modals/modal-pyramid'
import { getFilters } from './filters'
import ReturnToStageButton from './return-to-stage-button'
import { DeliveryDateModal } from '../card/modals/modal-delivery-date'
import { ResultStatusType } from 'antd/lib/result'
import { AssignZonesModal, MissingZoneOrder } from './modals/modal-assign-zones'

const createMenuCounters = createSelector(
  (state: any) => state.menuV2,
  counters => counters,
)

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

function parseUrlSearchParams(search: string): currentFiltersProps {
  const urlParams = new URLSearchParams(search)
  return {
    delivery_date: urlParams.get('delivery_date') || undefined,
    client_phone: urlParams.get('client_phone') || undefined,
    client_name: urlParams.get('client_name') || undefined,
    dealer: urlParams.get('dealer') || undefined,
    id: Number(urlParams.get('id')) || undefined,
    stage: urlParams.get('stage') || undefined,
    distribution_centre_name: urlParams.get('distribution_centre_name') || undefined,
    old_delivery_date: urlParams.get('old_delivery_date') || undefined,
    order_reference: urlParams.get('order_reference') || undefined,
    vehicle_id: Number(urlParams.get('vehicle_id')) || undefined,
    pyramid_number: Number(urlParams.get('pyramid_number')) || undefined,
    lifting: urlParams.get('lifting') || undefined,
    payment_verified: urlParams.get('payment_verified') || undefined,
    zone_exists: urlParams.get('zone_exists') || undefined,
    delivery_complexity: Number(urlParams.get('delivery_complexity')) || undefined,
    capacity:
      urlParams.get('capacity') == null || isNaN(Number(urlParams.get('capacity')))
        ? undefined
        : Number(urlParams.get('capacity')),
    volume:
      urlParams.get('volume') == null || isNaN(Number(urlParams.get('volume')))
        ? undefined
        : Number(urlParams.get('volume')),
    cost:
      urlParams.get('cost') == null || isNaN(Number(urlParams.get('cost')))
        ? undefined
        : Number(urlParams.get('cost')),
    type: urlParams.get('type') || undefined,
    debts: urlParams.get('debts') || undefined,
    shipment_amount:
      urlParams.get('shipment_amount[min]') || urlParams.get('shipment_amount[max]')
        ? {
            min: Number(urlParams.get('shipment_amount[min]')) || undefined,
            max: Number(urlParams.get('shipment_amount[max]')) || undefined,
          }
        : undefined,
  }
}

const DeliveriesList = () => {
  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('deliveriesPageSize')
    const parsedPageSize = Number(savedPageSize)

    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const navigate = useNavigate()
  const location = useLocation()

  const { sessionData } = useSelector(createSession)
  const isLeader = getDataOfType(sessionData, 'organization.id', Number, null) === 1
  const isManager = sessionData?.roles.includes(5)

  const [data, setData] = useState<DataType[]>([])
  const [vehicleSummaryData, setVehicleSummaryData] = useState<DataType[]>([])
  const [vehiclesData, setVehiclesData] = useState<any[]>([])
  const [shouldLoadRoutes, setShouldLoadRoutes] = useState(false)
  const [showRoutes, setShowRoutes] = useState(false)
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    manager: [],
    stage: [],
    organization: [],
    vehicle: [],
    pyramid_number: [],
    order_status_in_wd: [],
    distribution_centre_name: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(location.search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})
  const [loading, setLoading] = useState(true)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [stages, setStages] = useState<any>(null)
  const [sorter, setSorter] = useState<Sorter>({})
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })
  const [stats, setStats] = useState<{
    unapproved_count: number
    unverified_payment_count: number
  }>({
    unapproved_count: 0,
    unverified_payment_count: 0,
  })
  const { counters } = useSelector(createMenuCounters)
  const [localCounter, setLocalCounter] = useState<null | number>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isOpenVehicleModal, setIsOpenVehicleModal] = useState(false)
  const [isOpenPyramidModal, setIsOpenPyramidModal] = useState(false)
  const [isOpenDeliveryDateModal, setIsOpenDeliveryDateModal] = useState(false)
  const [isOpenUnloadFromWDModal, setIsOpenUnloadFromWDModal] = useState(false)
  const [isAssignZonesModalOpen, setIsAssignZonesModalOpen] = useState(false)
  const [assignZonesLoading, setAssignZonesLoading] = useState(false)
  const [missingZoneOrders, setMissingZoneOrders] = useState<MissingZoneOrder[]>([])
  const [enums, setEnums] = useState<any>({})
  const [fieldTypes, setFieldTypes] = useState<any>({})
  const [selectedDate, setSelectedDate] = useState<string>('')

  const headerRef = useRef<HTMLDivElement | null>(null)
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, location.pathname))
  const isInitialLoadRef = useRef(true)
  const isLoadingStagesRef = useRef(false)
  const isLoadingFiltersRef = useRef(false)
  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  useEffect(() => {
    const newFilters = parseUrlSearchParams(location.search)
    setCurrentFilters(newFilters)
  }, [location.search])

  useEffect(() => {
    const newCounterData = counters?.['/deliveries']
    if (newCounterData && !localCounter) {
      setLocalCounter(newCounterData)
    }
    if (newCounterData && localCounter && newCounterData !== localCounter) {
      setLocalCounter(newCounterData)
      getData()
    }
  }, [counters])

  const breadcrumbs = useMemo(() => {
    return [
      {
        href: '/logistics/deliveries',
        title: 'Доставки',
      },
    ]
  }, [])

  const getStages = useCallback(() => {
    if (!stages && !isLoadingStagesRef.current) {
      isLoadingStagesRef.current = true
      fetchAPI('/api/get-stages/delivery')
        .then(data => {
          setStages(data)
          isLoadingStagesRef.current = false
        })
        .catch(() => {
          console.error('Не удалось получить стадии')
          setError({ code: 'error', message: 'Не удалось получить стадии' })
          isLoadingStagesRef.current = false
        })
    }
  }, [stages])

  const deleteRecord = useCallback(
    (currentId: number) => {
      fetchAPI(`/api/logistics/deliveries/${currentId}`, {
        method: 'DELETE',
      })
        .then(() => {
          const urlParamsObj: any = Object.fromEntries(new URLSearchParams(location.search))
          if (!urlParamsObj.pageSize) {
            urlParamsObj.pageSize = initialPageSize
          }
          fetchAPI(`/api/list/delivery${objectToUrl(urlParamsObj)}`)
            .then(({ data, pagination: paginationData, sorter: sorterData, stats }) => {
              if (data && stages) {
                setData(
                  data.map((item: any) => ({
                    ...item,
                    stage: stages.find((stage: any) => stage.code === item.stage),
                  })),
                )
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
                if (sorterData) {
                  setSorter(sorterData)
                }
                if (stats) {
                  setStats({
                    unapproved_count: stats?.unapproved_count || 0,
                    unverified_payment_count: stats?.unverified_payment_count || 0,
                  })
                }
              }
            })
            .catch(() => {
              console.error('Не удалось обновить список после удаления')
            })
        })
        .catch(() => {
          console.error('Не удалось удалить запись')
        })
    },
    [location.search, stages, initialPageSize],
  )

  const summaryFilters = useMemo(() => {
    const urlParamsObj = Object.fromEntries(new URLSearchParams(location.search))
    const { page, pageSize, ...rest } = urlParamsObj
    return rest
  }, [location.search])

  const getVehicleSummaryData = useCallback(async () => {
    try {
      const urlParamsObj = {
        ...summaryFilters,
        pageSize: 'all',
      }

      const response = await fetchAPI(`/api/list/delivery${objectToUrl(urlParamsObj)}`)
      setVehicleSummaryData(
        response.data.map((item: any) => ({
          ...item,
        })),
      )

      const vehiclesResponse = await fetchAPI('/api/logistics/vehicles')
      setVehiclesData(vehiclesResponse.data || [])
    } catch (error) {
      console.error('Ошибка при получении данных для VehicleSummary:', error)
    }
  }, [selectedDate, summaryFilters])

  const getAvailableFilters = useCallback(() => {
    if (!stages || stages.length === 0 || isLoadingFiltersRef.current) {
      return
    }
    isLoadingFiltersRef.current = true
    fetchAPI(`/api/logistics/deliveries/enum-list`)
      .then(data => {
        setEnums(data)

        const filters = {
          stage: stages?.map((i: IStage) => ({ label: i.name, value: i.code })) || [],
          vehicle:
            data?.logistics_vehicle_id?.map((i: any) => ({ label: i.value, value: i.id })) || [],
          order_status_in_wd:
            data?.logistics_status?.map((i: any) => ({ label: i.value, value: i.id })) || [],
          pyramid_number:
            data?.pyramid_number?.map((i: any) => ({ label: i.value, value: i.id })) || [],
        }

        setAvailableFilters(filters)
        isLoadingFiltersRef.current = false
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
        isLoadingFiltersRef.current = false
      })
  }, [stages])

  const getFieldTypes = useCallback(() => {
    fetchAPI(`/api/logistics/deliveries/field-types`)
      .then(data => {
        setFieldTypes(data)
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
      })
  }, [])

  const getData = useCallback(async () => {
    if (!stages || stages.length === 0) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const urlParamsObj: any = Object.fromEntries(new URLSearchParams(location.search))

      if (!urlParamsObj.pageSize) {
        urlParamsObj.pageSize = initialPageSize
      }

      await fetchAPI(`/api/list/delivery${objectToUrl(urlParamsObj)}`)
        .then(async ({ data, pagination, sorter, stats }) => {
          if (data) {
            setData(
              data.map((item: any) => ({
                ...item,
                stage: stages.find((stage: any) => stage.code === item.stage),
              })),
            )

            setPagination(prevState => ({
              ...prevState,
              ...pagination,
            }))
            setSorter(sorter)

            setStats({
              unapproved_count: stats?.unapproved_count || 0,
              unverified_payment_count: stats?.unverified_payment_count || 0,
            })
          }
        })
        .catch(e => {
          setError({
            message: (e as IHttpError)?.message || 'Неизвестная ошибка',
            code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
          })
        })
        .finally(() => {
          setLoading(false)
        })
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
    }
  }, [stages, location.search, initialPageSize])

  const handleDeleteSelected = useCallback(() => {
    if (selectedRowKeys.length > 0) {
      Modal.confirm({
        title: 'Подтвердите действие',
        content: 'Вы уверены, что хотите удалить выбранные записи?',
        okText: 'Да',
        cancelText: 'Нет',
        onOk: async () => {
          try {
            const response = await fetchAPI('/api/logistics/deliveries/bulk-delete', {
              method: 'DELETE',
              body: JSON.stringify({ ids: selectedRowKeys }),
            })

            Modal.info({
              title: 'Результат удаления',
              content: (
                <div>
                  {response.shifted.length > 0 ? (
                    response.shifted.map(order => (
                      <div key={order.id}>
                        <a
                          href={`/logistics/deliveries/${order.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <b>{order.order_reference}</b>
                        </a>
                        : перенесён на очередь {order.newSequence}
                        {response.unapproved.some(u => u.id === order.id) && (
                          <span>
                            ,<b> снята отметка о согласовании</b>
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div>Изменений в очередности или согласовании не произошло</div>
                  )}
                </div>
              ),
              okText: 'ОК',
              width: 600,
            })

            message.success('Записи успешно удалены')
            setSelectedRowKeys([])
            getData()
          } catch (error) {
            message.error('Ошибка при удалении записей')
          }
        },
      })
    }
  }, [selectedRowKeys, getData])

  useEffect(() => {
    if (!stages) {
      getStages()
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0 && isInitialLoadRef.current) {
      getAvailableFilters()
      getFieldTypes()
      isInitialLoadRef.current = false
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0) {
      getData()
    }
  }, [location.search, stages])

  useEffect(() => {
    let newSelectedDate = ''
    if (currentFilters?.delivery_date) {
      const [startDate, endDate] = currentFilters.delivery_date.split('-')
      newSelectedDate = startDate === endDate ? startDate : ''
    }

    if (newSelectedDate && newSelectedDate !== selectedDate) {
      setSelectedDate(newSelectedDate)
      setShouldLoadRoutes(false)
      setShowRoutes(false)
    } else if (!newSelectedDate) {
      setSelectedDate('')
      setVehicleSummaryData([])
      setShouldLoadRoutes(false)
      setShowRoutes(false)
    }
  }, [currentFilters?.delivery_date, selectedDate])

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))
    setPagination(prevState => ({ ...prevState, ...pagination }))

    const current = {
      ...currentUrlParams,
      sort_order: sorter?.order,
      sort_field: sorter?.field,
    } as any

    if (pagination.current) {
      current.page = pagination.current
    }
    if (pagination.pageSize) {
      current.pageSize = pagination.pageSize
    }
    if (current?.page === 1) {
      delete current.page
    }
    if (current?.sort_order === undefined) {
      delete current.sort_order
      delete current.sort_field
    }

    const urlParams = new URLSearchParams(current).toString()
    navigate({ search: urlParams })
  }

  const handleRowClick = (record: DataType) => {
    const { id } = record
    navigate(`/logistics/deliveries/${id}`)
  }

  const onSelectChange = (selectedKeys: React.Key[]) => {
    setSelectedRowKeys(selectedKeys)
  }

  const changeFiltersHandler = useCallback(
    (filters: any) => {
      let tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize || 100 }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()

      const dates = filters?.delivery_date?.split('-') || []
      const newSelectedDate = dates[0] === dates[1] ? dates[0] : ''

      if (newSelectedDate) {
        setSelectedDate(newSelectedDate)
        setShouldLoadRoutes(false)
        setShowRoutes(false)
      } else if (!newSelectedDate) {
        setSelectedDate('')
        setVehicleSummaryData([])
        setShouldLoadRoutes(false)
        setShowRoutes(false)
      }

      navigate({ search: newUrlParams })
    },
    [pagination.pageSize, navigate],
  )

  const onSetApprovalMarkFilter = filters => {
    const { payment_verified, ...restFilters } = filters
    changeFiltersHandler(restFilters)
  }

  const onSetPaymentVerifiedFilter = filters => {
    const { approval_mark, ...restFilters } = filters
    changeFiltersHandler(restFilters)
  }

  const uploadProps = useMemo(() => {
    return {
      name: 'file',
      accept: '.csv',
      beforeUpload: handleUpload(getData, setLoading),
      showUploadList: false,
    }
  }, [getData])

  const columns = useMemo(() => getColumns(isLeader || isManager, sorter), [
    isLeader,
    isManager,
    sorter,
  ])

  const viewColumns = useMemo(() => {
    const defaultInput = { type: 'string' }

    const enumFieldsMapping = {
      pyramid_number: 'pyramid_number',
      vehicle: 'vehicle_id',
      zone: 'zone_id',
      culprit: 'culprit',
    }

    const enhanceColumn = (column: any) => {
      if (!('dataIndex' in column) || !column.editable) {
        return column
      }

      const dataIndex = enumFieldsMapping[column.dataIndex] || column.dataIndex

      const fieldType = fieldTypes[dataIndex]
      let input = fieldType || defaultInput

      if (fieldType?.view?.name === 'select-from-enum' && enums[dataIndex]) {
        const enumValues = enums[dataIndex]
        input = {
          ...input,
          key: dataIndex,
          enum: enumValues.map((item: { id: string; value: string }) => item.id),
          oneOf: enumValues.map((item: { id: string; value: string }) => ({
            const: item.id,
            title: item.value,
          })),
        }
      }

      return {
        ...column,
        input: {
          ...input,
          key: dataIndex,
        },
      }
    }

    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const enhancedColumns = columns.map(enhanceColumn)

      let result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = enhancedColumns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }
      return result
    }

    return columns.map(enhanceColumn)
  }, [columns, jsonSettings, fieldTypes, enums])

  const editableFields = useMemo(() => {
    return viewColumns.filter(column => column.editable).map(column => column.dataIndex as string)
  }, [viewColumns])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/list/delivery', new URLSearchParams(location.search))
  }, [location.search])

  const settingsColumns = useMemo(() => {
    return columns.map(column => ({
      key: 'dataIndex' in column ? column.dataIndex : undefined,
      title: column.title,
    }))
  }, [columns]) as TransferItem[]

  const onCancelVehicleModal = useMemo(() => handleCancelVehicleModal(setIsOpenVehicleModal), [])
  const onOkVehicleModal = useMemo(
    () =>
      handleOkVehicleModal({
        selectedRowKeys,
        setLoading,
        getData,
        setSelectedRowKeys,
        setIsOpenVehicleModal,
      }),
    [selectedRowKeys, setLoading, getData],
  )

  const onCancelPyramidModal = useMemo(() => handleCancelPyramidModal(setIsOpenPyramidModal), [])
  const onOkPyramidModal = useMemo(
    () =>
      handleOkPyramidModal({
        selectedRowKeys,
        setLoading,
        getData,
        setSelectedRowKeys,
        setIsOpenPyramidModal,
      }),
    [selectedRowKeys, setLoading, getData],
  )

  const onCancelDeliveryDateModal = useMemo(
    () => handleCancelDeliveryDateModal(setIsOpenDeliveryDateModal),
    [],
  )
  const onOkDeliveryDateModal = useMemo(
    () =>
      handleOkDeliveryDateModal({
        selectedRowKeys,
        setLoading,
        getData,
        setSelectedRowKeys,
        setIsOpenDeliveryDateModal,
      }),
    [selectedRowKeys, setLoading, getData],
  )

  const onCancelUnloadFromWDModal = useMemo(
    () => handleCancelUnloadFromWDModal(setIsOpenUnloadFromWDModal),
    [],
  )
  const onOkUnloadFromWDModal = useMemo(
    () =>
      handleOkUnloadFromWDModal({
        setLoading,
        getData,
        setSelectedRowKeys,
        setIsOpenUnloadFromWDModal,
      }),
    [],
  )

  const handleMissingZones = useCallback((orders: MissingZoneOrder[]) => {
    setMissingZoneOrders(orders)
    setIsAssignZonesModalOpen(true)
  }, [])

  const handleCloseAssignZones = useCallback(() => {
    setIsAssignZonesModalOpen(false)
    setMissingZoneOrders([])
  }, [])

  const handleSaveAssignZones = useCallback(
    async (rows: Array<{ id: number; zone_id: number }>) => {
      try {
        setAssignZonesLoading(true)
        await fetchAPI('/api/logistics/deliveries/update-group', {
          method: 'POST',
          body: JSON.stringify(rows),
          headers: { 'Content-Type': 'application/json' },
        })
        message.success('Территории сохранены')
        handleCloseAssignZones()
        getData()
      } catch (error) {
        message.error('Ошибка сохранения территорий')
      } finally {
        setAssignZonesLoading(false)
      }
    },
    [getData, handleCloseAssignZones],
  )

  const onCreate = useMemo(() => handleCreate(navigate), [navigate])

  const onUnloadFromWD = useMemo(() => handleUnloadFromWD(setIsOpenUnloadFromWDModal), [])

  const onSendToSchedex = useMemo(
    () =>
      handleSendToSchedex({
        selectedRowKeys,
        setSelectedRowKeys,
        setLoading,
        getData,
      }),
    [selectedRowKeys, getData],
  )

  const { onUnloadFromSchedex, onReloadFromSchedex } = useMemo(
    () => ({
      onUnloadFromSchedex: handleUnloadFromSchedex({
        selectedRowKeys,
        setSelectedRowKeys,
        setLoading,
        getData,
        isReload: false,
        onMissingZoneOrders: handleMissingZones,
      }),
      onReloadFromSchedex: handleUnloadFromSchedex({
        selectedRowKeys,
        setSelectedRowKeys,
        setLoading,
        getData,
        isReload: true,
        onMissingZoneOrders: handleMissingZones,
      }),
    }),
    [selectedRowKeys, getData, setSelectedRowKeys, setLoading, handleMissingZones],
  )

  const onSetPlanned = useMemo(
    () =>
      handleSetPlanned({
        selectedRowKeys,
        setSelectedRowKeys,
        setLoading,
        getData,
      }),
    [selectedRowKeys, getData],
  )

  const onSendMail = useMemo(
    () =>
      handleSendMail({
        selectedRowKeys,
        setSelectedRowKeys,
        setLoading,
        getData,
      }),
    [selectedRowKeys, getData],
  )

  const onSetVehicle = useMemo(() => handleSetVehicleModal(setIsOpenVehicleModal), [])

  const onSetPyramid = useMemo(() => handleSetPyramidModal(setIsOpenPyramidModal), [])

  const onSetDeliveryDate = useMemo(
    () => handleSetDeliveryDateModal(setIsOpenDeliveryDateModal),
    [],
  )

  const checkIsActionButtonEnabled = useMemo(() => isActionButtonEnabled(selectedRowKeys, data), [
    selectedRowKeys,
    data,
  ])

  const mostCommonPyramidNumber = useMemo(() => {
    if (selectedRowKeys.length === 0) return undefined

    const pyramidCount = selectedRowKeys
      .map(key => data.find(item => item.id === key))
      .filter(delivery => delivery?.pyramid_number != null)
      .map(delivery => delivery!.pyramid_number)
      .reduce((acc, pyramid) => {
        if (pyramid != null) {
          acc[pyramid] = (acc[pyramid] || 0) + 1
        }
        return acc
      }, {} as { [key: number]: number })

    if (Object.keys(pyramidCount).length === 0) return undefined

    return Object.keys(pyramidCount)
      .map(Number)
      .reduce((a, b) => (pyramidCount[a] > pyramidCount[b] ? a : b))
  }, [selectedRowKeys, data])

  const changeDateFilterHandler = (newDate: string) => {
    const urlParams = new URLSearchParams(location.search)

    if (newDate) {
      urlParams.set('delivery_date', `${newDate}-${newDate}`)
    } else {
      urlParams.delete('delivery_date')
    }

    urlParams.delete('approval_mark')
    urlParams.delete('payment_verified')

    setSelectedDate(newDate)
    setShouldLoadRoutes(false)
    setShowRoutes(false)
    setVehicleSummaryData([])

    urlParams.set('page', '1')

    setPagination(prevState => ({
      ...prevState,
      current: 1,
    }))

    navigate({ search: urlParams.toString() })
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('deliveriesPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(location.search)
    currentUrlParams.set('page', page.toString())
    currentUrlParams.set('pageSize', pageSize.toString())
    navigate({ search: currentUrlParams.toString() })
  }

  const onSaveEdit = handleSaveEdit({
    setLoading,
    getData,
    setSelectedRowKeys,
  })

  const totalItemsCount = useMemo(() => {
    return data
      .filter(item => item?.delivery_date)
      .reduce((sum, record) => Number(sum) + (Number(record.cost) || 0), 0)
  }, [data])

  const selectedOrders = useMemo(() => {
    return selectedRowKeys
      .map(key => {
        const order = data.find(item => item.id === Number(key))
        return order ? { id: Number(key), stage: order.stage?.code || '' } : null
      })
      .filter(Boolean) as { id: number; stage: string }[]
  }, [selectedRowKeys, data])

  const approvalMarkFilters = useMemo(
    () => [
      {
        name: `Подтвердить дату доставки у <b>${stats.unapproved_count}</b> ${declension(
          stats.unapproved_count,
          'заказа',
          'заказов',
          'заказов',
        )}`,
        code: 'n',
      },
    ],
    [stats.unapproved_count],
  )

  const paymentVerifiedFilters = useMemo(
    () => [
      {
        name: `Проверить сумму оплаты у <b>${stats.unverified_payment_count}</b> ${declension(
          stats.unverified_payment_count,
          'заказа',
          'заказов',
          'заказов',
        )}`,
        code: 'n',
      },
    ],
    [stats.unverified_payment_count],
  )

  const dataWithActions = useMemo(() => {
    return data.map(item => ({
      ...item,
      deleteRecord,
    }))
  }, [data, deleteRecord])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton pageTitle={'Доставки'}>
      {error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!error && stages && (
        <>
          <FixedHeader ref={headerRef}>
            <Space
              style={{ marginBottom: '12px', width: '100%', justifyContent: 'space-between' }}
              align={'start'}
              wrap
            >
              <Space wrap>
                <ActionButtons
                  onUnloadFromWD={onUnloadFromWD}
                  uploadProps={uploadProps}
                  onSendToSchedex={onSendToSchedex}
                  onUnloadFromSchedex={onUnloadFromSchedex}
                  onReloadFromSchedex={onReloadFromSchedex}
                  onSetPlanned={onSetPlanned}
                  onSetVehicle={onSetVehicle}
                  onSetPyramid={onSetPyramid}
                  onSetDeliveryDate={onSetDeliveryDate}
                  onSendMail={onSendMail}
                  isSendToSchedexEnabled={checkIsActionButtonEnabled(['new'])}
                  isUnloadFromSchedexEnabled={checkIsActionButtonEnabled(['sent_to_schedex'])}
                  isReloadFromSchedexEnabled={checkIsActionButtonEnabled([
                    'uploaded_from_schedex',
                    'planned',
                    'exported_to_wd',
                  ])}
                  isSetPlannedEnabled={checkIsActionButtonEnabled(['uploaded_from_schedex'])}
                  isSetVehicleEnabled={checkIsActionButtonEnabled(['planned', 'exported_to_wd'])}
                  isSetPyramidEnabled={checkIsActionButtonEnabled(['uploaded_from_schedex'])}
                  isSetDeliveryDateEnabled={selectedRowKeys.length > 0}
                  isSendMailEnabled={selectedRowKeys.length > 0}
                />
                {stages && (
                  <ReturnToStageButton
                    selectedOrders={selectedOrders}
                    stages={stages}
                    onSuccess={getData}
                  />
                )}
                <Space wrap>
                  <DateFilter selectedDate={selectedDate} onChange={changeDateFilterHandler} />
                  <FilterFastButtons
                    groupName="distribution_centre_name"
                    filters={[
                      {
                        name: 'Рязань',
                        code: 'Рязань',
                      },
                      {
                        name: 'Калуга',
                        code: 'Калуга',
                      },
                    ]}
                    onChange={changeFiltersHandler}
                  />
                  <FilterFastButtons
                    groupName="debts"
                    filters={[
                      {
                        name: `Долги и довозы`,
                        code: 'y',
                      },
                    ]}
                    onChange={changeFiltersHandler}
                  />
                </Space>
              </Space>
              <FiltersDrawer
                filters={getFilters({ currentFilters, availableFilters })}
                onChange={changeFiltersHandler}
                currentFilters={currentFilters}
              />
            </Space>

            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap align={'start'}>
              <Space wrap>
                <FilterFastButtons filters={stages} onChange={changeFiltersHandler} />
                <Space wrap>
                  {selectedDate && currentFilters.debts !== 'y' && stats.unapproved_count > 0 && (
                    <FilterFastButtons
                      groupName="approval_mark"
                      filters={approvalMarkFilters}
                      onChange={onSetApprovalMarkFilter}
                    />
                  )}
                  {selectedDate &&
                    currentFilters.debts !== 'y' &&
                    stats?.unverified_payment_count > 0 && (
                      <FilterFastButtons
                        groupName="payment_verified"
                        filters={paymentVerifiedFilters}
                        onChange={onSetPaymentVerifiedFilter}
                      />
                    )}
                </Space>
              </Space>

              <Space style={{ width: '100%', justifyContent: 'flex-end' }} align={'start'}>
                <HStack gap={'8'} align={'start'}>
                  <DeliveriesPrintButton path={excelPath} columns={viewColumns} stages={stages} />
                  <TableSettingsButton items={settingsColumns} />
                </HStack>
              </Space>
            </Space>
          </FixedHeader>
          {selectedDate && (
            <div style={{ marginBottom: 16, marginTop: 16 }}>
              <Button
                type={shouldLoadRoutes && showRoutes ? 'primary' : 'default'}
                icon={shouldLoadRoutes && showRoutes ? <EyeInvisibleOutlined /> : <CarOutlined />}
                onClick={() => {
                  if (!shouldLoadRoutes) {
                    setShouldLoadRoutes(true)
                    setShowRoutes(true)
                    getVehicleSummaryData()
                  } else {
                    setShowRoutes(!showRoutes)
                  }
                }}
              >
                {!shouldLoadRoutes
                  ? 'Загрузить маршруты'
                  : showRoutes
                  ? 'Скрыть маршруты'
                  : 'Показать маршруты'}
              </Button>
            </div>
          )}
          {selectedDate && shouldLoadRoutes && showRoutes && (
            <VehicleSummaryTiles
              data={vehicleSummaryData}
              currentFilters={currentFilters}
              vehiclesData={vehiclesData}
              shouldLoadSchedules={shouldLoadRoutes}
            />
          )}
          <DataTable
            columns={viewColumns}
            data={dataWithActions}
            loading={loading}
            rowKey={r => String(r.id)}
            highlightLastRowPrefix={'delivery'}
            rowClassName={record => {
              if (record.type === 'Сбор') return 'blue'
              if (record.type === 'Довоз') return 'pink'
              if (record.type === 'Долг') return 'pink'
              if (record.pyramid_number != null) {
                const pyramidClass = String(record.pyramid_number)
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                return `pyramid-${pyramidClass}`
              }
              return ''
            }}
            pagination={pagination}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            onRowClick={handleRowClick}
            onCreate={onCreate}
            onDelete={handleDeleteSelected}
            onPageChange={handlePageChange}
            showDelete
            onTableChange={handleTableChange}
            className={'delivery-table'}
            headerRef={headerRef}
            editableFields={editableFields}
            onSaveEdit={onSaveEdit}
            maxLines={1}
            bordered
            extraFooterInfo={
              selectedDate ? (
                <Space wrap>
                  <span>
                    Кол-во изделий: <b>{totalItemsCount}</b>
                  </span>
                </Space>
              ) : null
            }
          />

          <VehicleModal
            isModalOpen={isOpenVehicleModal}
            onCancel={onCancelVehicleModal}
            onOk={onOkVehicleModal}
            enums={enums}
            selectedPyramid={mostCommonPyramidNumber}
          />

          <PyramidModal
            isModalOpen={isOpenPyramidModal}
            onCancel={onCancelPyramidModal}
            onOk={onOkPyramidModal}
            enums={enums}
          />

          <DeliveryDateModal
            isModalOpen={isOpenDeliveryDateModal}
            onCancel={onCancelDeliveryDateModal}
            onOk={onOkDeliveryDateModal}
          />

          <UnloadFromWDModal
            isModalOpen={isOpenUnloadFromWDModal}
            onCancel={onCancelUnloadFromWDModal}
            onOk={onOkUnloadFromWDModal}
          />

          <AssignZonesModal
            isModalOpen={isAssignZonesModalOpen}
            orders={missingZoneOrders}
            zones={enums?.zone_id || []}
            loading={assignZonesLoading}
            onCancel={handleCloseAssignZones}
            onOk={handleSaveAssignZones}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default DeliveriesList
